/**
 * profileNudge.js
 * 
 * Gating logic for the "Complete Your Profile" nudge modal.
 * 
 * Design principle: Never feel "pop-upy" or needy.
 * 
 * Show nudge ONLY if ALL are true:
 * - Profile completion <= threshold (default 20%)
 * - Not within 2 minutes of a recent interruption event (onboarding, modals, etc.)
 * - Not already shown this session (tab lifetime)
 * - Not dismissed in last 7 days
 * 
 * This turns time-based gating into behavior-based gating.
 */

// localStorage keys
const INTERRUPT_KEY = "ul_last_interrupt_at";
const DISMISS_KEY = "ul_profile_nudge_dismissed_at";

// sessionStorage key (resets on tab close)
const SEEN_SESSION_KEY = "ul_profile_nudge_seen_session";

// Cooldowns
const INTERRUPT_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes after any interruption
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days after dismissal

/**
 * Check if the profile nudge can be shown.
 * 
 * @param {Object} options
 * @param {number} options.completionPct - Current profile completion percentage
 * @param {number} options.thresholdPct - Max completion to show nudge (default 20)
 * @returns {boolean} True if nudge can be shown
 */
export function canShowProfileNudge({ completionPct, thresholdPct = 20 }) {
  try {
    // Guard: must have completion data
    if (completionPct == null) return false;
    
    // Guard: completion above threshold
    if (completionPct > thresholdPct) return false;

    const now = Date.now();

    // Guard: dismissed within last 7 days
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || "0");
    if (dismissedAt && now - dismissedAt < DISMISS_COOLDOWN_MS) return false;

    // Guard: recent interruption event (onboarding closed, modal closed, etc.)
    const lastInterruptAt = Number(localStorage.getItem(INTERRUPT_KEY) || "0");
    if (lastInterruptAt && now - lastInterruptAt < INTERRUPT_COOLDOWN_MS) return false;

    // Guard: already shown this session (tab lifetime)
    if (sessionStorage.getItem(SEEN_SESSION_KEY) === "1") return false;

    return true;
  } catch {
    // If localStorage/sessionStorage fails, don't show nudge
    return false;
  }
}

/**
 * Mark that the nudge was shown this session.
 * Prevents showing again until tab is closed/refreshed.
 */
export function markProfileNudgeShownThisSession() {
  try {
    sessionStorage.setItem(SEEN_SESSION_KEY, "1");
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.error("[profileNudge] sessionStorage set failed:", err);
  }
}

/**
 * Mark that the user dismissed the nudge.
 * Prevents showing again for 7 days.
 */
export function markProfileNudgeDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.error("[profileNudge] dismiss set failed:", err);
  }
}

/**
 * Clear the dismissal (e.g., if user clicks "Complete Now" - they're engaging)
 */
export function clearProfileNudgeDismissal() {
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.error("[profileNudge] dismiss clear failed:", err);
  }
}

/**
 * Record an interruption event (onboarding closed, modal closed, etc.)
 * This prevents nudge from showing for 2 minutes after.
 */
export function recordInterruptionEvent() {
  try {
    localStorage.setItem(INTERRUPT_KEY, String(Date.now()));
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.error("[profileNudge] interrupt set failed:", err);
  }
}
